/*
 * Copyright (c) 2019  Apricad LLC. All Rights Reserved.
 */

import { Action, Dispatch } from "redux";
import axios from "axios";

import {
  GET_COURSES,
  GET_CURRENT_COURSE,
  GET_COURSE_IMAGE,
  GET_CURRENT_LECTURE,
  FIND_LECTURES,
  CLEAR_LEC_FOUND,
  GET_COURSES_FAILED,
  GET_COURSE_FAILED,
  GET_LECTURES_FAILED,
  GET_CURRENT_LECTURE_FAILED,
  ApricotActionCreator,
  PHOENIX_NOP,
  reqHeader,
} from "./types";
import {
  allCoursesLoaded,
  currentCourseLoaded,
  currentLectureLoaded,
} from "../selectors/courseSelector";
import { sAccessCourseFail } from "assets/jss/views/ResStrings.tsx";

const inTest = process.env.NODE_ENV === "test";

// the 'api' passed in is the axiosInstance we created
export const getCourses: ApricotActionCreator = (query: any) => async (
  dispatch: Dispatch<any>,
  getState,
  api,
): Promise<Action> => {
  if (!inTest && allCoursesLoaded(getState())) {
    return Promise.resolve() as any;
  }

  try {
    api = api || axios; // in Test, api is not passed in.
    const res = await api({
      method: "post",
      url: `/api/courses/${query || ""}`,
      headers: reqHeader(getState),
    });

    return dispatch({
      type: GET_COURSES,
      payload: res.data,
    });
  } catch (err) {
    return dispatch({
      type: GET_COURSES_FAILED,
      payload: sAccessCourseFail,
    });
  }
};

export const getCurrentCourse: ApricotActionCreator = (id: string) => async (
  dispatch: Dispatch<any>,
  getState,
  api,
): Promise<Action> => {
  // call selector with state and mimic props.match.params.id
  if (!inTest && currentCourseLoaded(getState(), { id })) {
    return Promise.resolve() as any;
  }

  try {
    api = api || axios;
    const res = await api.get("/api/course/" + id);

    return dispatch({
      type: GET_CURRENT_COURSE,
      payload: res.data,
    });
  } catch (err) {
    return dispatch({
      type: GET_COURSE_FAILED,
      payload: sAccessCourseFail,
    });
  }
};

export const getCurrentLecture: ApricotActionCreator = (lid: string) => async (
  dispatch: Dispatch<any>,
  getState,
  api,
): Promise<Action> => {
  if (lid === "style.css") {
    // ??? - hack: for issue the 'style.css' passed in as an id
    return dispatch({ type: PHOENIX_NOP, payload: null });
  }

  // call selector with state and mimic props.match.params.lid
  if (!inTest && currentLectureLoaded(getState(), { lid })) {
    return Promise.resolve() as any;
  }

  try {
    api = api || axios;
    const res = await api.get("/api/lecture/" + lid);

    return dispatch({
      type: GET_CURRENT_LECTURE,
      payload: res.data,
    });
  } catch (err) {
    return dispatch({
      type: GET_CURRENT_LECTURE_FAILED,
      payload: sAccessCourseFail,
    });
  }
};

export const findLectures: ApricotActionCreator = (
  uid: string,
  keywords: string,
) => async (dispatch: Dispatch<any>, getState, api): Promise<Action> => {
  try {
    api = api || axios;
    const user = uid || "x";
    const kw = keywords ? `?kw=${keywords.split(" ").join(",")}` : "";

    const res = await api.get(`/api/lectures/${user}${kw}`);
    return dispatch({
      type: FIND_LECTURES,
      payload: res.data,
    });
  } catch (err) {
    return dispatch({
      type: GET_LECTURES_FAILED,
      payload: sAccessCourseFail,
    });
  }
};

export const clearLecFound: ApricotActionCreator = () => async (
  dispatch: Dispatch<any>,
  getState,
  api,
): Promise<Action> => {
  return dispatch({
    type: CLEAR_LEC_FOUND,
    payload: null,
  });
};

export const getImage: ApricotActionCreator = (imgInfo) => async (
  dispatch: Dispatch<any>,
  getState,
  api,
): Promise<Action> => {
  api = api || axios;
  const { imgPath, cid } = imgInfo;
  const res = await api.get(`/api/file/ca?imgpath=${imgPath}`);

  return dispatch({
    type: GET_COURSE_IMAGE,
    payload: { cid, image: res.data },
  });
};

export type ApricotAction = ApricotActionCreator;
